import { resolveDefaultWeight, type WeightSource } from "./resolveDefaultWeight.js";
import type { Exercise } from "./schemas/exercise.js";
import type { PlannedExerciseEntry } from "./schemas/plan.js";

// Bridge type between the plan-list surface ("Start" trigger) and the
// logger (form prefill consumer). The trigger side resolves percentage
// targets to kg against the user's live MAXes before firing
// pushPendingStart; the logger only has to render the prefilled values
// into its existing weighted-set form.
//
// fromPlanId is preserved through to the workout save payload so the
// API can flip the plan row to status=completed. For program-started
// workouts there's no plan to complete, so planId stays absent.
//
// Lifted from apps/web/src/plans/start.ts so the mobile Plan tab can
// build the same starter shape from the same percent-of-MAX +
// bodyweight conversion. Pure TS, no React, no DOM.

export type PlanStarter = {
  planId?: string;
  /** Workout-level title carried into the logger draft so the saved
   *  session is identifiable in history without the user retyping it.
   *  120-char cap mirrors Workout.name; trigger sites truncate before
   *  firing. */
  name?: string;
  /** datetime-local format, see the web logger; mobile derives the
   *  same shape from new Date().toISOString().slice(0, 16). */
  startedAtLocal: string;
  notes?: string;
  /** Read-only coach context for the logger's workout-level "?" panel.
   *  Resolved at Start from the program/plan template (program description +
   *  the day's session note, or a single plan's note). Display-only — NEVER
   *  flows into the save payload, so it can't become a denormalized snapshot
   *  on the saved session. Absent for ad-hoc/manual workouts. */
  coachNotes?: { programDescription?: string; sessionNote?: string };
  entries: {
    exerciseId: string;
    notes?: string;
    /** Per-entry RPE-column default (Logger redesign slice 3). True
     *  means the Logger opens with the RPE column visible for this
     *  entry. Propagated from PlannedExerciseEntry.rpeEnabled (which
     *  in turn carries Slot.rpeEnabled via programResolution). */
    rpeEnabled?: boolean;
    /** Per-entry RIR-column default (Phase 8). Mirror of rpeEnabled.
     *  Propagated from PlannedExerciseEntry.rirEnabled. */
    rirEnabled?: boolean;
    /** Forwarded to WorkoutEntry.group when the logger materialises
     *  this starter into a draft. ≥2 entries that share an id form
     *  a superset or circuit window. */
    group?: { id: string; kind: "superset" | "circuit" };
    sets: {
      reps: number;
      weightKg: number; // already-resolved canonical kg
      /** Provenance of the prefilled weight. Stamped by
       *  resolveDefaultWeight in buildPlanStarter. Omitted only when
       *  the chain returned `'blank'` (no prescription + no history) —
       *  the logger treats absence as "leave the cell empty". Forwarded
       *  onto DraftSet.weightSource on hydrate, then to LogSet.weightSource
       *  on save (data-only, no UI cue). */
      weightSource?: WeightSource;
      rpe?: number;
      /** RIR target (0..10) from a "rir"-kind program slot. Mirror of
       *  rpe; a planned set carries at most one. The logger pre-fills
       *  whichever matches the athlete's effortScale, converting the
       *  other via effortScale.ts at hydrate time. */
      rir?: number;
      /** AMRAP-on-last-set hint from the program resolver. Carried
       *  through so the logger can render "X+ reps" placeholder. */
      amrap?: boolean;
      /** Coach's per-set prescription cue (SetPrescription.notes, 200 cap):
       *  e.g. "last set AMRAP if technique is solid", "OHP reintroduced —
       *  RIR2-3 (shoulder)". Display-only, read-only; rendered under the set
       *  row. Not persisted — the athlete's own diary is the timeline note. */
      notes?: string;
    }[];
  }[];
};

export type StarterContext = {
  /** exerciseId → MAX kg, freshly fetched at "Start" time so a MAX
   *  update between draft and execution shifts the targets. */
  maxes: Map<string, number>;
  /** bodyweight at "Start" time, null when the user hasn't set a
   *  value. Used to convert percent-of-MAX targets on bodyweight
   *  exercises into an "extra" delta (effective − bw). */
  bodyweightKg: number | null;
  /** exerciseId → most recently logged weighted kg for the athlete on
   *  that exercise. Drives the 5-step resolveDefaultWeight chain: a
   *  percent-of-MAX set with no recorded MAX, an explicit
   *  `kind: 'history'` set, and a bare `kind: 'none'` set all fall
   *  back to this map. Optional with default `new Map()` so existing
   *  callsites that don't yet plumb a history fetch keep compiling;
   *  those just get the `'blank'` fallback (regression-equivalent to
   *  pre-Phase-1 behaviour, no behaviour change). Callers populate
   *  best-effort — empty entries are fine, the chain treats missing
   *  slugs as "no history". */
  lastLoggedKgs?: Map<string, number>;
  /** Read-only slice of the shared catalog: only the fields needed
   *  to detect isBodyweight per exercise. */
  catalogItems: Pick<Exercise, "slug" | "isBodyweight">[];
};

const round2 = (x: number) => Math.round(x * 100) / 100;

export const buildPlanStarter = (params: {
  planId?: string;
  name?: string;
  notes?: string;
  coachNotes?: { programDescription?: string; sessionNote?: string };
  startedAtLocal: string;
  entries: PlannedExerciseEntry[];
  ctx: StarterContext;
}): PlanStarter => {
  const { planId, name, notes, coachNotes, startedAtLocal, entries, ctx } =
    params;
  // Workout.name caps at 120 chars; truncate here so program-composed
  // titles never bounce off the schema validator at save time. We trim
  // trailing whitespace so a mid-word slice doesn't leave a dangling
  // separator.
  const trimmedName = name?.trim().slice(0, 120).trim();
  const hasCoachNotes =
    coachNotes !== undefined &&
    (coachNotes.programDescription !== undefined ||
      coachNotes.sessionNote !== undefined);
  return {
    ...(planId !== undefined ? { planId } : {}),
    ...(trimmedName ? { name: trimmedName } : {}),
    startedAtLocal,
    ...(notes ? { notes } : {}),
    ...(hasCoachNotes ? { coachNotes } : {}),
    entries: entries.map((entry) => {
      const ex = ctx.catalogItems.find((c) => c.slug === entry.exerciseId);
      const isBw = ex?.isBodyweight === true;
      return {
        exerciseId: entry.exerciseId,
        ...(entry.notes ? { notes: entry.notes } : {}),
        ...(entry.rpeEnabled === true ? { rpeEnabled: true } : {}),
        ...(entry.rirEnabled === true ? { rirEnabled: true } : {}),
        ...(entry.group
          ? { group: { id: entry.group.id, kind: entry.group.kind } }
          : {}),
        sets: entry.sets.map((s) => {
          const resolved = resolveDefaultWeight(s.targetWeight, {
            userMax: ctx.maxes.get(entry.exerciseId),
            lastLoggedKg: ctx.lastLoggedKgs?.get(entry.exerciseId),
          });
          // Bodyweight convention: a MAX recorded against a bodyweight
          // exercise is the TOTAL load (bw + extra), so the prescribed
          // EXTRA shown in the logger is total − bw. Only applies when
          // the chain came from the percent-of-MAX step (the rounded
          // kg is the total). 'history' / 'prescribed-absolute' values
          // ride through as-is — history is already an extra, absolute
          // is whatever the coach typed.
          let kg = resolved.kg;
          if (
            resolved.source === "computed-percent" &&
            isBw &&
            ctx.bodyweightKg !== null
          ) {
            kg = round2(kg - ctx.bodyweightKg);
          }
          return {
            reps: s.targetReps,
            weightKg: kg,
            ...(resolved.source !== "blank"
              ? { weightSource: resolved.source }
              : {}),
            ...(s.targetRpe !== undefined ? { rpe: s.targetRpe } : {}),
            ...(s.targetRir !== undefined ? { rir: s.targetRir } : {}),
            ...(s.amrap === true ? { amrap: true } : {}),
            ...(s.notes ? { notes: s.notes } : {}),
          };
        }),
      };
    }),
  };
};
